import { Box, Flex, useColorModeValue } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import CDS from "design-system";
import { format, parseISO } from "date-fns";
import { API_ROUTES, INTERNAL_TEAM_ROUTES } from "definitions/constants/routeConstants";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import apiClient from "services/ApiClient";
import ProfileAvatar from "components/ProfileAvatar";

interface StartingJourney {
  id: string | number;
  newHireName: string | null;
  avatarUrl: string | null;
  title: string | null;
  startDate: string | null;
  issueCount: number;
  openTaskCount: number;
  overdueTaskCount: number;
}

interface StartingData {
  thisWeek: StartingJourney[];
  nextWeek: StartingJourney[];
}

function statusDotColor(hire: StartingJourney): string {
  if (hire.issueCount > 0) return "#DC2626";
  if (hire.overdueTaskCount > 0) return "#D97706";
  if (hire.openTaskCount > 0) return "#3B82F6";
  return "#16A34A";
}

function HireCard({ hire }: { hire: StartingJourney }) {
  const navigate = useNavigate();
  const journeyPath = INTERNAL_TEAM_ROUTES.newHireJourneys.show.replace(":id", String(hire.id));
  const startDate = hire.startDate ? format(parseISO(hire.startDate), "MMM d") : null;
  const fakeUser = { avatarUrl: hire.avatarUrl ?? undefined, fullName: hire.newHireName ?? "" } as any;
  const dotColor = statusDotColor(hire);
  const tagBg = useColorModeValue("white", "gray.700");

  return (
    <Flex
      align="center"
      gap="6px"
      flexShrink={0}
      h="28px"
      px="10px"
      borderRadius="6px"
      bg="gray.50"
      border="1px solid"
      borderColor="gray.200"
      cursor="pointer"
      onClick={() => navigate(journeyPath)}
      _hover={{ bg: "gray.100" }}
      transition="background 0.12s"
    >
      <ProfileAvatar user={fakeUser} name={hire.newHireName ?? ""} size="3xs" flexShrink={0} />
      <CDS.Text12
        as={Link}
        {...{ to: journeyPath }}
        fontWeight="600"
        color="content.primary"
        noOfLines={1}
        lineHeight="1.3"
        _hover={{ textDecoration: "underline" }}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        flexShrink={1}
        minW={0}
      >
        {hire.newHireName ?? "—"}
      </CDS.Text12>
      {startDate && (
        <Box
          flexShrink={0}
          px="4px"
          py="0px"
          borderRadius="4px"
          bg={tagBg}
          border="1px solid"
          borderColor="#D1D5DB"
          fontSize="9px"
          fontWeight="500"
          color="#6B7280"
          lineHeight="1.6"
          whiteSpace="nowrap"
        >
          {startDate}
        </Box>
      )}
      <Box w="7px" h="7px" borderRadius="full" bg={dotColor} flexShrink={0} />
    </Flex>
  );
}

const VISIBLE_CAP = 5;

function MoreChip({ count, onClick }: { count: number; onClick: () => void }) {
  return (
    <Box
      as="button"
      onClick={onClick}
      flexShrink={0}
      h="28px"
      px="10px"
      borderRadius="6px"
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      _hover={{ bg: "gray.50" }}
      transition="background 0.12s"
      cursor="pointer"
      fontSize="12px"
      fontWeight="600"
      color="content.primary"
      display="flex"
      alignItems="center"
      whiteSpace="nowrap"
    >
      +{count} more
    </Box>
  );
}

function StartingRow({ label, hires }: { label: string; hires: StartingJourney[] }) {
  const [expanded, setExpanded] = useState(false);
  const overflow = hires.length - VISIBLE_CAP;
  const hasMore = overflow > 0;
  const shown = expanded ? hires : hires.slice(0, VISIBLE_CAP);

  return (
    <Flex align="stretch" minH="42px">
      <Box position="relative" flexShrink={0} alignSelf="stretch">
        <Flex direction="column" justify="center" px="14px" h="100%" minW="90px">
          <CDS.Text10
            fontSize="8px"
            fontWeight="500"
            color="content.tertiary"
            textTransform="uppercase"
            letterSpacing="0.08em"
            lineHeight="1.5"
            whiteSpace="nowrap"
          >
            Starting
          </CDS.Text10>
          <CDS.Text10
            fontSize="9px"
            fontWeight="700"
            color="content.primary"
            textTransform="uppercase"
            letterSpacing="0.06em"
            lineHeight="1.5"
            whiteSpace="nowrap"
          >
            {label}
          </CDS.Text10>
        </Flex>
      </Box>

      <Flex
        gap="8px"
        px="10px"
        py="6px"
        align="center"
        flex={1}
        flexWrap={expanded ? "wrap" : "nowrap"}
        overflow={expanded ? undefined : "hidden"}
      >
        {shown.map((hire) => (
          <HireCard key={hire.id} hire={hire} />
        ))}
        {hasMore && !expanded && <MoreChip count={overflow} onClick={() => setExpanded(true)} />}
        {expanded && (
          <Box
            as="button"
            onClick={() => setExpanded(false)}
            flexShrink={0}
            h="28px"
            px="10px"
            borderRadius="6px"
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            _hover={{ bg: "gray.50" }}
            transition="background 0.12s"
            cursor="pointer"
            fontSize="12px"
            fontWeight="600"
            color="content.tertiary"
            display="flex"
            alignItems="center"
          >
            Show less
          </Box>
        )}
      </Flex>
    </Flex>
  );
}

export default function StartingCard() {
  const { data } = useQuery<StartingData>({
    queryKey: ["dashboardStarting"],
    queryFn: () => apiClient.get(API_ROUTES.dashboard.starting),
    staleTime: 1000 * 60 * 3,
  });

  if (!data) return null;
  if (data.thisWeek.length === 0 && data.nextWeek.length === 0) return null;

  return (
    <Box bg="white" borderRadius="10px" overflow="hidden" border="1px solid" borderColor="gray.100">
      {data.thisWeek.length > 0 && <StartingRow label="This Week" hires={data.thisWeek} />}
      {data.thisWeek.length > 0 && data.nextWeek.length > 0 && <Box h="1px" bg="gray.100" />}
      {data.nextWeek.length > 0 && <StartingRow label="Next Week" hires={data.nextWeek} />}
    </Box>
  );
}
